import {
  FormatStyleType,
  QuranReaderType,
  QuranMushaf,
  QuranResponseType,
  QuranStateType,
  TranslationsListType,
  UseQuranOptionProps,
  QuranProps,
  QuranResponsePageType,
} from "../types";
import { format, intervalToDuration } from "date-fns";
import { Database, getDbColumns } from "./database";
import { UseIndexedDBResult } from "@/lib/hooks/useIndexedDb";
import { getMushafPage } from "@/actions/get-mushaf-page";
import { getMushafPageFromPagenumber } from "@/actions/get-mushaf-page-from-pagenumber";

//TODO convert this function into a hook
const setCookie = (cname: string, cvalue: string, exdays: number) => {
  const d = new Date();
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
  let expires = "expires=" + d.toUTCString();
  document.cookie =
    cname + "=" + cvalue + ";" + expires + ";SameSite=Strict;Secure;path=/";
};

//TODO convert this function into a hook
const debounce = (
  func: React.Dispatch<React.SetStateAction<any>>,
  timeout: number = 300,
) => {
  let timer: NodeJS.Timeout;
  return (...args: any) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, timeout);
  };
};

//TODO convert this function into a hook
const delay = async (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

const sort = (
  arr: Array<any>,
  type: "name" | "number",
  order: "asc" | "des",
  sortField: string,
) => {
  return type == "number"
    ? sortOnNum(arr, 0, arr.length - 1, order, sortField)
    : sortOnString(arr, 0, arr.length - 1, 0, order, sortField);
};

const swap = (s: any, i: number, j: number) => {
  var temp = s[i];
  s[i] = s[j];
  s[j] = temp;
};

const sortOnNum = (
  s: any,
  lo: number,
  hi: number,
  order: "asc" | "des",
  field: string,
): Array<any> | undefined => {
  if (hi <= lo) return;

  var lt = lo,
    gt = hi;

  var v = s[Math.floor((lo + hi) / 2)][field];
  var i = lo;
  while (i <= gt) {
    var c = s[i][field];
    if (order === "asc") {
      if (c < v) swap(s, lt++, i++);
      else if (c > v) swap(s, i, gt--);
      else i++;
    }
    if (order === "des") {
      if (c > v) swap(s, lt++, i++);
      else if (c < v) swap(s, i, gt--);
      else i++;
    }
  }
  sortOnNum(s, lo, lt - 1, order, field);
  sortOnNum(s, gt + 1, hi, order, field);

  return s;
};

const sortOnString = (
  s: any,
  lo: number,
  hi: number,
  d: number,
  order: "asc" | "des",
  field: string,
) => {
  const charAt = (s: string, d: number) => {
    if (d < s.length) return s.charCodeAt(d);
    else return -1;
  };

  if (hi <= lo) return;
  var lt = lo,
    gt = hi;
  var v = charAt(s[lo][field], d);
  var i = lo + 1;
  while (i <= gt) {
    var c = charAt(s[i][field], d);
    if (order === "asc") {
      if (c < v) swap(s, lt++, i++);
      else if (c > v) swap(s, i, gt--);
      else i++;
    }
    if (order === "des") {
      if (c > v) swap(s, lt++, i++);
      else if (c < v) swap(s, i, gt--);
      else i++;
    }
  }
  sortOnString(s, lo, lt - 1, d, order, field);
  if (v >= 0) sortOnString(s, lt, gt, d + 1, order, field);
  sortOnString(s, gt + 1, hi, d, order, field);

  return s;
};

const combineWithCondition = <T, K>(
  targetArray: Array<T>,
  targetProp: keyof T,
  sourceArray: Array<K>,
  sourceProp: keyof K,
  joinField: keyof T,
  asArray: boolean = true,
  objProp?: string,
) => {
  let sourceIndex = 0;

  for (let i = 0; i < targetArray.length; i++) {
    const targetItem = targetArray[i];
    const targetValue = targetItem[targetProp];

    const existing = targetItem[joinField];
    const combinedItem = { ...targetItem };

    if (objProp) {
      combinedItem[joinField] = { ...existing };
    }
    if (asArray) {
      combinedItem[joinField] = Array.isArray(existing)
        ? ([...existing] as T[keyof T])
        : ([] as T[keyof T]);
    }

    for (let j = sourceIndex; j < sourceArray.length; j++, sourceIndex++) {
      const sourceItem = sourceArray[j];
      const sourceValue = sourceItem[sourceProp];

      if (
        typeof targetValue === "number" &&
        typeof sourceValue === "number" &&
        sourceValue > targetValue
      ) {
        break;
      }

      if (asArray) (combinedItem[joinField] as K[]).push({ ...sourceItem });
      else if (objProp) {
        const key = objProp;
        combinedItem[joinField] = {
          ...combinedItem[joinField],
          [key]: sourceItem,
        };
      }
    }

    targetArray[i] = combinedItem;
  }

  return targetArray;
};

const transLanguages = async (db: ReturnType<typeof Database>) => {
  let languages;

  languages = await db<[{ language: string }]>()
    .select("distinct language")
    .from("translations_list")
    .orderBy("language")
    .orderDirection("DESC")
    .execute();

  languages.sort((a, b) => (a.language < b.language ? -1 : 1));

  return languages;
};

const availableLanguageTranslations = async (
  db: ReturnType<typeof Database>,
  languages: [{ language: string }],
) => {
  let result: Array<TranslationsListType[]> = [];

  await Promise.all(
    languages.map(async (lang) => {
      const res = await db<TranslationsListType[]>()
        .select("*")
        .from("translations_list")
        .where(`language = '${lang.language}'`)
        .execute();

      result.push(res);
    }),
  );

  // db().close();
  return result;
};

function generateRandomString(length = 256) {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    result += characters[randomIndex];
  }
  return result;
}

// Generate a random 256-character string
function formatFileSize(bytes: number, decimals: number = 2) {
  if (bytes === 0) return "0 Bytes";

  const k = 1000; // Use 1024 for binary
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
}

function formatFileType(type: string) {
  return type.slice(0, type.lastIndexOf("/"));
}

function formatDateWithTime(value: string | number | Date) {
  return format(value, "PP hh:mm a");
}

function formatDateShort(value: string | number | Date) {
  return format(value, "MMM dd");
}

function formatTime(value: string | number | Date) {
  return format(value, "h:mm a");
}

function formatDuration(value: string | number | Date) {
  const numberValue = Number(value);
  const isNegative = numberValue < 0;
  const absoluteValue = Math.abs(numberValue);

  const duration = intervalToDuration({ start: 0, end: absoluteValue });

  const hours = duration.hours ? `${duration.hours}h` : "";
  const minutes = duration.minutes ? `${duration.minutes}m` : "";
  const seconds = duration.seconds ? `${duration.seconds}s` : "";

  const formattedDuration = `${hours} ${minutes} ${seconds}`.trim();

  return isNegative ? `-${formattedDuration}` : formattedDuration;
}

function camelCaseToTitleCase(camelCaseStr: string) {
  const titleCaseStr = camelCaseStr
    .replace(/([A-Z])/g, " $1") // Insert space before uppercase letters
    .replace(/^./, (char) => char.toUpperCase()); // Capitalize the first letter

  return titleCaseStr;
}

function titleCaseToCamelCase(titleCaseStr: string) {
  const camelCaseStr = titleCaseStr
    .toLowerCase() // Convert the entire string to lowercase first
    .replace(/\s+(.)/g, (_, char) => char.toUpperCase()); // Remove spaces and capitalize the following character

  return camelCaseStr;
}

//TODO:--------------------TODO--------------------
//change the type here to loacale later when implementing locales
function formatPercent(value: number, locales: string = "en") {
  return new Intl.NumberFormat(locales, {
    style: "percent",
    maximumFractionDigits: 0,
  }).format(value);
}

function isNonNegative(num: number) {
  return num >= 0;
}

function formatOverviewCardValue(
  value: number,
  formatStyle: FormatStyleType,
): string | number {
  switch (formatStyle) {
    case "percent":
      return formatPercent(value);
    case "duration":
      return formatDuration(value);
    default:
      return value.toLocaleString("en", {
        maximumFractionDigits: 0,
      });
  }
}

const fetchViewAyahPage = async ({
  getRecord,
  saveRecords,
  setAyah,
  activeTranslations,
  item,
  quranMushaf,
  pageNumber,
  data,
  localStorage,
}: {
  getRecord: UseIndexedDBResult<QuranResponseType[]>["getRecord"];
  saveRecords: UseIndexedDBResult<QuranResponseType>["saveRecords"];
  quranMushaf: QuranMushaf;
  setAyah: React.Dispatch<React.SetStateAction<QuranResponseType | null>>;
  activeTranslations: Array<TranslationsListType>;
  data: QuranStateType["data"];
  pageNumber: number;
  item: { surah: number; verse: number };
  localStorage: { start: number; end: number }[] | undefined;
}) => {
  let mushafPage: QuranResponseType[] = [];
  let localStoragePages: { start: number; end: number }[] | undefined =
    undefined;
  switch (quranMushaf.type) {
    case "indopak16":
    case "indopak15":
      if (quranMushaf.lines == 15 || quranMushaf.lines == 16) {
        if (localStorage)
          if (data.current["IndoPak"][quranMushaf.lines][pageNumber]) {
            mushafPage = data.current["IndoPak"][quranMushaf.lines][pageNumber];
          } else {
            const res = await getRecord(`${quranMushaf.type}`, pageNumber);

            if (!res) {
              const resp = await fetch(
                `/api/get-page?mushaf=${quranMushaf.type}&surah=${item.surah}&verse=${item.verse}&num_pages=1&active=${JSON.stringify(activeTranslations.map((e) => e.id))}`,
              );
              const serverResult = await resp.json();

              //   await getMushafPage(
              //     quranMushaf.type,
              //     item.surah,
              //     item.verse,
              //     quranMushaf.lines,
              //     false,
              //     1,
              //     activeTranslations.map((e) => e.id),
              //   )
              // ).data;

              saveRecords(quranMushaf.type, serverResult.data);
              mushafPage = serverResult[pageNumber];
            } else {
              mushafPage = res;
            }
          }
        else {
          const resp = await fetch(
            `/api/get-page?mushaf=${quranMushaf.type}&surah=${item.surah}&verse=${item.verse}&pages_index=true&num_pages=1&active=${JSON.stringify(activeTranslations.map((e) => e.id))}`,
          );
          const serverResult = await resp.json();
          // const serverResult = await getMushafPage(
          //   quranMushaf.type,
          //   item.surah,
          //   item.verse,
          //   quranMushaf.lines,
          //   true,
          //   1,
          //   activeTranslations.map((e) => e.id),
          // );

          localStoragePages = serverResult.pages;
          saveRecords(quranMushaf.type, serverResult.data);
          pageNumber = parseInt(Object.keys(serverResult.data)[0]);
          mushafPage = serverResult.data[pageNumber];
        }
      }
      break;
    case "hafs2":
    case "hafs1":
      if (localStorage) {
        if (data.current["Uthmani"][quranMushaf.type][pageNumber]) {
          mushafPage = data.current["Uthmani"][quranMushaf.type][pageNumber];
        } else {
          const res = await getRecord(`${quranMushaf.type}`, pageNumber);

          if (!res) {
            const resp = await fetch(
              `/api/get-page?mushaf=${quranMushaf.type}&surah=${item.surah}&verse=${item.verse}&num_pages=1&active=${JSON.stringify(activeTranslations.map((e) => e.id))}`,
            );
            const serverResult = await resp.json();

            // const serverResult = (
            //   await getMushafPage(
            //     quranMushaf.type,
            //     item.surah,
            //     item.verse,
            //     quranMushaf.lines,
            //     false,
            //     1,
            //     activeTranslations.map((e) => e.id),
            //   )
            // ).data;

            saveRecords(quranMushaf.type, serverResult.data);
            mushafPage = serverResult[pageNumber];
            console.log(serverResult);
          } else {
            mushafPage = res;
          }
        }
      } else {
        const resp = await fetch(
          `/api/get-page?mushaf=${quranMushaf.type}&pages_index=true&surah=${item.surah}&verse=${item.verse}&num_pages=1&active=${JSON.stringify(activeTranslations.map((e) => e.id))}`,
        );
        const serverResult = await resp.json();
        //
        // const serverResult = await getMushafPage(
        //   quranMushaf.type,
        //   item.surah,
        //   item.verse,
        //   quranMushaf.lines,
        //   true,
        //   1,
        //   activeTranslations.map((e) => e.id),
        // );
        localStoragePages = serverResult.pages;
        saveRecords(quranMushaf.type, serverResult.data);
        pageNumber = parseInt(Object.keys(serverResult.data)[0]);
        console.log(serverResult);
        mushafPage = serverResult.data[pageNumber];
      }
    default:
      break;
  }

  setDataCache(quranMushaf, data, { [pageNumber]: mushafPage });

  for (var i = 0; i < mushafPage.length; i++) {
    if (
      mushafPage[i].surah == item.surah &&
      mushafPage[i].verse == item.verse
    ) {
      setAyah(mushafPage[i]);
      break;
    }
  }

  return localStoragePages;
};

const setDataCache = (
  quranMushaf: QuranMushaf,
  data: QuranStateType["data"],
  result: { [key: string]: QuranResponseType[] },
) => {
  switch (quranMushaf.type) {
    case "indopak15":
    case "indopak16":
      if (quranMushaf.lines == 15 || quranMushaf.lines == 16)
        data.current.IndoPak[quranMushaf.lines] = {
          ...data.current.IndoPak[quranMushaf.lines],
          ...result,
        };
      break;
    case "hafs2":
    case "hafs1":
    default:
      data.current.Uthmani[quranMushaf.type] = {
        ...data.current.Uthmani[quranMushaf.type],
        ...result,
      };
  }
};

const findPageNumberFromLocalStorage = (
  id: number,
  localStorage: { start: number; end: number }[] | undefined,
) => {
  let i = 1;
  if (localStorage) {
    for (; i < localStorage.length; i++) {
      const page = localStorage[i];
      const num = id;
      if (page.start <= num && page.end >= num) break;
    }
  }

  return i;
};

//These changes can lead to possible function breakup
const getViewData = async (
  view: QuranProps["activeViewRef"],
  get: 1 | -1,
  data: UseQuranOptionProps["data"],
  mushaf: UseQuranOptionProps["quranMushaf"],
  getRecord: UseIndexedDBResult<QuranResponseType[]>["getRecord"],
  saveRecords: UseIndexedDBResult<QuranResponseType>["saveRecords"],
  cb: (
    data: { [key: string]: QuranResponseType[] },
    view?: QuranProps["activeViewRef"],
  ) => QuranResponseType[] | QuranResponsePageType[],
  activeTranslation: Array<TranslationsListType>,
) => {
  const { page } = view.current;
  if (page == 0 || page + get < 1) return { data: cb({}, view), server: false };

  let dataToSend: { [key: string]: QuranResponseType[] } = {};
  let maybeServer: { data: ReturnType<typeof cb>; server: boolean } = {
    data: [],
    server: false,
  };

  switch (mushaf.type) {
    case "indopak16":
    case "indopak15":
      if (mushaf.lines == 15 || mushaf.lines == 16) {
        if (Object.hasOwn(data, page + get)) {
          dataToSend[page + get] =
            data.current["IndoPak"][mushaf.lines][page + get];
        } else {
          const res = await getRecord(`${mushaf.type}`, page + get);

          if (!res) {
            const resp = await fetch(
              `/api/get-page-number?mushaf=${mushaf.type}&page=${page + get}&direction=${get}&active=${JSON.stringify(activeTranslation.map((e) => e.id))}`,
            );
            const result = await resp.json();

            //   await getMushafPageFromPagenumber(
            //     mushaf.type,
            //     page + get,
            //     get,
            //     activeTranslation.map((e) => e.id),
            //   )
            // ).data;

            maybeServer.server = true;
            saveRecords(mushaf.type, result.data);
            setDataCache(mushaf, data, result.data);
            dataToSend[page + get] =
              data.current["IndoPak"][mushaf.lines][page + get];
          } else {
            dataToSend = { [page + get]: res };
            setDataCache(mushaf, data, dataToSend);
          }
        }
      }
      break;
    case "hafs2":
    case "hafs1":
      if (Object.hasOwn(data, page + get)) {
        dataToSend[page + get] =
          data.current["Uthmani"][mushaf.type][page + get];
      } else {
        const res = await getRecord(`${mushaf.type}`, page + get);

        if (!res) {
          console.log(
            `/api/get-page-number?mushaf=${mushaf.type}&page=${page + get}&direction=${get}&active=${JSON.stringify(activeTranslation.map((e) => e.id))}`,
          );
          const resp = await fetch(
            `/api/get-page-number?mushaf=${mushaf.type}&page=${page + get}&direction=${get}&active=${JSON.stringify(activeTranslation.map((e) => e.id))}`,
          );
          const result = await resp.json();

          //   await getMushafPageFromPagenumber(
          //     mushaf.type,
          //     page + get,
          //     get,
          //     activeTranslation.map((e) => e.id),
          //   )
          // ).data;
          //
          maybeServer.server = true;
          saveRecords(mushaf.type, result.data);
          setDataCache(mushaf, data, result.data);
          dataToSend[page + get] =
            data.current["Uthmani"][mushaf.type][page + get];
        } else {
          dataToSend = { [page + get]: res };
          setDataCache(mushaf, data, dataToSend);
        }
      }

    default:
      break;
  }
  maybeServer.data = cb(dataToSend, view);
  return maybeServer;
};

const emitViewLoadingEvent = (val = true) => {
  const eventDetail = {
    value: val,
    type: "viewLoading",
  };

  const event = new CustomEvent("viewLoading", {
    detail: eventDetail,
  });

  window.dispatchEvent(event);
};


export {
  emitViewLoadingEvent,
  getViewData,
  findPageNumberFromLocalStorage,
  setDataCache,
  fetchViewAyahPage,
  setCookie,
  debounce,
  delay,
  sort,
  combineWithCondition,
  availableLanguageTranslations,
  transLanguages,
  generateRandomString,
  formatFileSize,
  formatFileType,
  formatDateShort,
  formatTime,
  formatDuration,
  formatDateWithTime,
  formatPercent,
  camelCaseToTitleCase,
  titleCaseToCamelCase,
  isNonNegative,
  formatOverviewCardValue,
};
